import React, { useRef, useState } from 'react';
import { Dropdown } from 'antd';
import { Link } from 'react-router-dom';

import { CHEVRON_DOWN, COLLECTION_ICON } from '../icons';
import { Skeleton } from '../loading';
import CollectionSwitcher from './CollectionSwitcher';
import useWindowClass from '../../hooks/useWindowClass';

const CRUMB_ICONS = { collection: COLLECTION_ICON };

/**
 * On a phone the middle of a long path folds into one "…" node holding a menu
 * (docs/platform-standards.md §13.2): the first crumb (where the platform
 * starts) and the last (the current object) always stay, everything between
 * them goes behind the menu, in order. Two crumbs, or three or more on a wide
 * screen, are left alone. Pure, so the rule is testable without a screen.
 */
export function collapseCrumbs(crumbs, { phone = false } = {}) {
	const list = Array.isArray(crumbs) ? crumbs.filter(Boolean) : [];
	if (!phone || list.length < 3) return list;
	return [list[0], { ellipsis: true, label: '…', items: list.slice(1, -1) }, list[list.length - 1]];
}

// What the "…" menu lists: a link for an ancestor the viewer can open, a
// plain greyed name for one they cannot (never a link that answers 404).
const menuItemFor = (crumb, index) => ({
	key: `${crumb.label}-${index}`,
	label: crumb.to ? <Link to={crumb.to}>{crumb.label}</Link> : crumb.label,
	disabled: !crumb.to,
});

const SWITCH_KEY = 'switch-collection';

/**
 * WHERE AM I (§13.2): the slim breadcrumb above the badge. The shell draws it
 * from the config's crumbs; the last node is the current object and is never a
 * link, and no crumb is marked current (the active tab is, so the page has one
 * `aria-current`).
 */
export default function ShellCrumbs({ crumbs = [] }) {
	// compact (§18) folds the middle of a long path into one "…".
	const { compact: phone } = useWindowClass();
	const nodes = collapseCrumbs(crumbs, { phone });
	// The collection switcher (KPR-640): open state lives here so the "…" menu a
	// phone folds the path into can open it too when the collection is folded away.
	const [switcherOpen, setSwitcherOpen] = useState(false);
	const triggerRef = useRef(null);
	if (!nodes.length) return null;
	const lastIndex = nodes.length - 1;
	const switcherCrumb = crumbs.find(crumb => crumb?.switcher && crumb.collectionId);
	const switcherFolded = Boolean(switcherCrumb) && nodes.some(node => node.ellipsis && node.items.includes(switcherCrumb));

	return (
		<nav className="shell-header__crumbs" aria-label="Breadcrumb" data-shell-crumbs>
			<ol>
				{nodes.map((crumb, index) => {
					const separator = index > 0 ? <span className="shell-header__crumb-sep" aria-hidden="true">›</span> : null;

					if (crumb.ellipsis) {
						return (
							<li key="ellipsis" className="shell-header__crumb" data-shell-crumb-ellipsis>
								{separator}
								<Dropdown trigger={['click']} placement="bottomLeft" menu={{
									items: [
										...crumb.items.map(menuItemFor),
										...(switcherFolded ? [{ type: 'divider' }, { key: SWITCH_KEY, label: 'Switch collection' }] : []),
									],
									onClick: ({ key }) => { if (key === SWITCH_KEY) setSwitcherOpen(true); },
								}}>
									<button type="button" className="shell-header__crumb-more" aria-label="Show the rest of the path" aria-haspopup="menu">…</button>
								</Dropdown>
							</li>
						);
					}

					const Icon = crumb.icon ? CRUMB_ICONS[crumb.icon] : null;
					const first = index === 0;
					const body = (
						<>
							{Icon ? <Icon className="shell-header__crumb-icon" aria-hidden="true" /> : null}
							{crumb.pending ? (
								<span className="shell-header__crumb-label shell-header__crumb-label--pending" aria-hidden="true">
									<Skeleton variant="line" width={96} />
								</span>
							) : (
								<span className={Icon ? 'shell-header__crumb-label shell-header__crumb-label--collapsible' : 'shell-header__crumb-label'}>
									{crumb.label}
								</span>
							)}
						</>
					);
					const switcher = crumb.switcher && crumb.collectionId ? (
						<CollectionSwitcher
							collectionId={crumb.collectionId}
							open={switcherOpen}
							onOpenChange={setSwitcherOpen}
							triggerRef={triggerRef}
							trigger={(
								<button
									ref={triggerRef}
									type="button"
									className="shell-header__crumb-switch"
									aria-label={`Switch collection, current: ${crumb.label}`}
									aria-haspopup="dialog"
									aria-expanded={switcherOpen}
									data-shell-switcher-trigger
								>
									<CHEVRON_DOWN aria-hidden="true" />
								</button>
							)}
						/>
					) : null;
					return (
						<li key={`${crumb.label}-${index}`} className="shell-header__crumb">
							{separator}
							{crumb.to && index !== lastIndex
								? <Link to={crumb.to} aria-label={first && Icon ? crumb.label : undefined} title={crumb.label}>{body}</Link>
								: <span className="shell-header__crumb-current" title={crumb.label}>{body}</span>}
							{switcher}
						</li>
					);
				})}
			</ol>
			{switcherFolded ? (
				<CollectionSwitcher collectionId={switcherCrumb.collectionId} open={switcherOpen} onOpenChange={setSwitcherOpen} triggerRef={triggerRef} />
			) : null}
		</nav>
	);
}
